import { useEffect, useState } from 'react';
import {
  DropDown,
  Flex,
  FlexColumn,
  FormControls,
  InfoTooltip,
} from '@elementary/common-ui/components';
import { AnomalySensitivity } from '@elementary/common-ui/types';
import { Controller, UseFormReturn } from 'react-hook-form';
import PercentageThresholdField from 'shared/components/PercentageThresholdField';
import {
  AnomalyDirectionField,
  DetectionModeField,
  DetectionPeriodField,
} from 'shared/components/VolumeAnomalyConfigSections';
import { anomalyDirectionOptions } from 'shared/constants';
import { AddTestForm, VolumeDetectionMode } from 'shared/types';
import { sensitivityOptions } from 'shared/constants';
import { themeTokens } from '@elementary/common-ui/style/tokens';
import { space } from '@elementary/common-ui/style/utils';
import {
  DEFAULT_ANOMALY_TRAINING_PERIOD,
  isUnsetPeriodCount,
} from '../constants';
import SeveritySelector from '../SeveritySelector';
import TestMetadataForm from './shared/TestMetadataForm';
import WizardTrainingPeriodSection from './shared/WizardTrainingPeriodSection';

type Props = {
  methods: UseFormReturn<AddTestForm, any, undefined>;
  skipDefaults?: boolean;
};

const TablesVolumeAnomaliesCloud = ({ methods, skipDefaults }: Props) => {
  const [expanded, setExpanded] = useState(true);
  const mode: VolumeDetectionMode =
    methods.watch('volumeDetectionMode') ?? 'anomaly_detection';

  const anomalyDirection = methods.watch('anomalyConfig.anomalyDirection');

  const handleModeChange = (next: VolumeDetectionMode) => {
    methods.setValue('volumeDetectionMode', next);
  };

  useEffect(() => {
    if (skipDefaults) return;

    if (methods.getValues('anomalyConfig.anomalyDirection') == null) {
      methods.setValue(
        'anomalyConfig.anomalyDirection',
        anomalyDirectionOptions[0].value as 'BOTH' | 'DROP' | 'SPIKE',
      );
    }
    if (methods.getValues('anomalyConfig.sensitivity') == null) {
      methods.setValue(
        'anomalyConfig.sensitivity',
        sensitivityOptions[1].value as AnomalySensitivity,
      );
    }
    if (
      isUnsetPeriodCount(
        methods.getValues('anomalyConfig.detectionPeriod.count'),
      )
    ) {
      methods.setValue('anomalyConfig.detectionPeriod.count', 2);
      methods.setValue('anomalyConfig.detectionPeriod.period', 'day');
    }
    if (
      isUnsetPeriodCount(
        methods.getValues('anomalyConfig.trainingPeriod.count'),
      )
    ) {
      methods.setValue(
        'anomalyConfig.trainingPeriod.count',
        DEFAULT_ANOMALY_TRAINING_PERIOD.count,
      );
      methods.setValue(
        'anomalyConfig.trainingPeriod.period',
        DEFAULT_ANOMALY_TRAINING_PERIOD.period,
      );
    }
    if (methods.getValues('volumeDetectionMode') == null) {
      methods.setValue('volumeDetectionMode', 'anomaly_detection');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skipDefaults]);

  return (
    <FormControls.TestAccordion
      summary="Volume anomalies"
      expanded={expanded}
      onChange={(_, val) => setExpanded(val)}
    >
      <FlexColumn sx={{ gap: 3, p: 3 }}>
        <DetectionModeField mode={mode} onChange={handleModeChange} />

        <AnomalyDirectionField
          control={methods.control}
          name="anomalyConfig.anomalyDirection"
        />

        {mode === 'percentage_thresholds' && (
          <FormControls.FieldsWrapper style={{ gap: themeTokens.space.sm }}>
            <Flex sx={{ alignItems: 'center', gap: space('xs') }}>
              <FormControls.FormTitle>
                Fixed percentage thresholds
              </FormControls.FormTitle>
              <InfoTooltip
                size={16}
                title={
                  <>
                    Compared to the previous measurement.
                    <br />
                    Example: a 3% drop threshold fails when 100 → 97.
                  </>
                }
              />
            </Flex>

            <PercentageThresholdField
              control={methods.control}
              anomalyDirection={anomalyDirection}
              spikeFieldName="anomalyConfig.spikeThreshold"
              dropFieldName="anomalyConfig.dropThreshold"
              shouldUnregister={false}
            />
          </FormControls.FieldsWrapper>
        )}

        {mode === 'anomaly_detection' && (
          <FormControls.FieldsWrapper style={{ gap: themeTokens.space.sm }}>
            <FormControls.FormTitle>Sensitivity</FormControls.FormTitle>

            <FormControls.Row
              style={{ gap: themeTokens.space.sm, flexWrap: 'wrap' }}
            >
              <FormControls.FormText>
                Adjust test thresholds by setting the sensitivity level to be
              </FormControls.FormText>
              <Controller
                control={methods.control}
                name="anomalyConfig.sensitivity"
                render={({ field }) => (
                  <DropDown
                    sortOptions={false}
                    options={sensitivityOptions}
                    {...field}
                  />
                )}
              />
            </FormControls.Row>
          </FormControls.FieldsWrapper>
        )}

        <DetectionPeriodField
          control={methods.control}
          register={methods.register}
          countName="anomalyConfig.detectionPeriod.count"
          periodName="anomalyConfig.detectionPeriod.period"
        />

        <WizardTrainingPeriodSection methods={methods} />

        <FormControls.FieldsWrapper>
          <Controller
            name="severity"
            control={methods.control}
            defaultValue={{ type: 'warn' }}
            render={({ field }) => <SeveritySelector {...field} />}
          />
        </FormControls.FieldsWrapper>

        <TestMetadataForm methods={methods} />
      </FlexColumn>
    </FormControls.TestAccordion>
  );
};

export default TablesVolumeAnomaliesCloud;
